import React, { useMemo, useState } from "react";
import { EmptyStateErro } from "../../components/EmptyState";
import PainelTabela from "../../components/PainelTabela";
import { PageLoader } from "../../components/LoadingMarcenaria";
import {
  CargaPorProjetista,
  CelulaInicio,
  CelulaProjetista,
} from "../../components/ProjetistaExecutivo";
import AtribuirEmLote from "../../components/ProjetistaExecutivo/AtribuirEmLote";
import {
  SEM_PROJETISTA,
  atribuicaoDe,
  bloqueioDoProjetista,
  dataLocal,
} from "../../components/ProjetistaExecutivo/regras";
import { useDados } from "../../context/DadosContext";
import useProjetistasExecutivo from "../../hooks/useProjetistasExecutivo";
import { fmtDiasEmFila } from "../../utils/calculos";
import { isNo } from "../../utils/data";
import { F } from "../../utils/gerencialFields";
import { indiceDeIdentidade } from "../../utils/identidadeAmbiente";
import { buildDeadlineStatus, compareDatesAsc } from "../../utils/status";

const COLUNAS = [
  { key: "cliente", label: "Cliente", noWrap: true },
  { key: "ambiente", label: "Ambiente" },
  // Projetista e início não existem na planilha: vêm do backend
  // (`useProjetistasExecutivo`). O valor da linha é texto/data (ordenação,
  // filtro por coluna e exportação); o `render` põe o controle de edição.
  {
    key: "projetista",
    label: "Projetista",
    labelCurto: "projetista",
    width: "170px",
    render: ({ row }) => <CelulaProjetista linha={row} />,
  },
  {
    key: "inicioExecutivo",
    label: "Início",
    labelCurto: "início",
    tipo: "data",
    width: "150px",
    render: ({ row }) => <CelulaInicio linha={row} />,
  },
  { key: "entregaContrato", label: "Entrega Prevista", labelCurto: "entrega", tipo: "data", width: "110px" },
  { key: "executivoLimite", label: "Executivo Limite", labelCurto: "limite", tipo: "data", width: "110px" },
  { key: "valor", label: "Valor", labelCurto: "valor", tipo: "moeda", width: "110px" },
  { key: "diasEmFila", label: "Dias em fila", labelCurto: "fila", width: "90px" },
  { key: "status", label: "Status", width: "100px" },
];

export default function ElaboracaoExecutivo() {
  const { dados, erro, initialLoading } = useDados();
  const { atribuicoes } = useProjetistasExecutivo();
  // Ficha de carga clicada: nome do projetista, SEM_PROJETISTA ou null (todos).
  const [filtroProjetista, setFiltroProjetista] = useState(null);
  const [loteAberto, setLoteAberto] = useState(false);

  // Memoizado (e antes dos early-returns — Rules of Hooks): sem isso, cada
  // re-render recriava `linhas` (filter+map+sort de 400+ itens) e a nova
  // referência invalidava toda a cadeia de memos do PainelTabela.
  const linhasDaPlanilha = useMemo(
    () =>
      dados
        .filter((row) => {
          const naoFutura = isNo(row[F.OBRA_FUTURA]);
          const temLiberacao = Boolean(row[F.LIBERACAO_DA_OBRA]);
          const temAcabamentos = Boolean(row[F.DEFINICAO_ACABAMENTOS_REALIZADO]);
          const temMedicao = Boolean(row[F.MEDICAO_REALIZADO]);
          const semExecutivo = !row[F.EXECUTIVO_REALIZADO];

          return naoFutura && temLiberacao && temAcabamentos && temMedicao && semExecutivo;
        })
        .map((row) => {
          const executivoLimite = row[F.EXECUTIVO_LIMITE];
          // entrou na fila de executivo quando o ultimo pre-requisito ficou pronto
          // (acabamentos E medicao); usa a data mais recente entre os dois
          const entradaExecutivo = [
            row[F.DEFINICAO_ACABAMENTOS_REALIZADO],
            row[F.MEDICAO_REALIZADO],
          ]
            .filter(Boolean)
            .sort(compareDatesAsc)
            .pop();

          return {
            cliente: row[F.NOME_DO_CLIENTE],
            rowPlanilha: row,
            ambiente: row[F.AMBIENTE],
            entregaContrato: row[F.ENTREGA_PREVISTA],
            executivoLimite,
            valor: row[F.VALOR_DO_AMBIENTE],
            diasEmFila: fmtDiasEmFila(entradaExecutivo),
            ...buildDeadlineStatus(executivoLimite),
          };
        })
        .sort((left, right) => compareDatesAsc(left.executivoLimite, right.executivoLimite)),
    [dados],
  );

  const indice = useMemo(() => indiceDeIdentidade(dados), [dados]);

  // Junta o que o backend sabe (projetista e início) a cada linha da planilha.
  const linhas = useMemo(
    () =>
      linhasDaPlanilha.map((linha) => {
        const atribuicao = atribuicaoDe(linha.rowPlanilha, atribuicoes);
        return {
          ...linha,
          projetista: atribuicao?.projetista || "",
          projetistaId: atribuicao?.projetista_id || null,
          inicioExecutivo: dataLocal(atribuicao?.inicio),
          inicioIso: atribuicao?.inicio || "",
          bloqueioProjetista: bloqueioDoProjetista(linha.rowPlanilha, indice),
        };
      }),
    [linhasDaPlanilha, atribuicoes, indice],
  );

  const linhasVisiveis = useMemo(() => {
    if (!filtroProjetista) return linhas;
    const alvo = filtroProjetista === SEM_PROJETISTA ? "" : filtroProjetista;
    return linhas.filter((linha) => linha.projetista === alvo);
  }, [linhas, filtroProjetista]);

  const totalizadores = useMemo(
    () => [
      { label: "Total de Ambientes", valor: linhasVisiveis.length },
      {
        label: "Em Atraso",
        valor: linhasVisiveis.filter((linha) => linha.statusVariant === "danger").length,
      },
    ],
    [linhasVisiveis],
  );

  if (initialLoading) {
    return <PageLoader scope="executivo" />;
  }

  if (erro) {
    return <EmptyStateErro erro={erro} />;
  }

  return (
    <>
      <PainelTabela
        titulo="Elaboração de Executivo"
        exportPainelId="executivo"
        colunas={COLUNAS}
        linhas={linhasVisiveis}
        totalizadores={totalizadores}
        conteudoAcimaTotalizadores={
          <CargaPorProjetista
            linhas={linhas}
            filtro={filtroProjetista}
            onFiltro={setFiltroProjetista}
            onLote={() => setLoteAberto(true)}
          />
        }
        densidade="compact"
        mostrarPaginacao={false}
        persistirNoBackend
      />
      <AtribuirEmLote aberto={loteAberto} onFechar={() => setLoteAberto(false)} linhas={linhasVisiveis} />
    </>
  );
}
